import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Text,
} from '@ltvco/refresh-lib/theme';
import { Close } from '@mui/icons-material';

import { usePatentDocuments } from './api';
import {
  DocumentsTabHeading,
  MetadataItem,
  MetadataLabel,
  MetadataValue,
  ModalApplicationNumber,
  ModalAppType,
  ModalBody,
  ModalTitle,
  PatentDetailsDialog,
} from './components';
import { PatentDocumentsList } from './PatentDocumentsList';
import { formatPatentApplicationNumber, formatPatentDate } from './utils';

import type { PatentApplicationResult } from './types';

interface PatentDetailsModalProps {
  patent: PatentApplicationResult | null;
  open: boolean;
  onClose: () => void;
}

interface LatestOfficialDateRecord {
  source: string;
  timestamp: number;
}

const getLatestOfficialDate = (
  patentDocuments: { officialDate?: string }[]
): string | null => {
  const validDates = patentDocuments
    .map((document) => {
      if (!document.officialDate) {
        return null;
      }
      const timestamp = new Date(document.officialDate).getTime();
      if (Number.isNaN(timestamp)) {
        return null;
      }
      return { source: document.officialDate, timestamp };
    })
    .filter((record): record is LatestOfficialDateRecord => record !== null);

  if (validDates.length === 0) {
    return null;
  }

  return validDates.reduce((latest, current) =>
    current.timestamp > latest.timestamp ? current : latest
  ).source;
};

interface MetadataFieldProps {
  label: string;
  value?: string | null;
}

const MetadataField = ({ label, value }: MetadataFieldProps) => {
  if (!value) {
    return null;
  }
  return (
    <MetadataItem>
      <MetadataLabel>{label}</MetadataLabel>
      <MetadataValue>{value}</MetadataValue>
    </MetadataItem>
  );
};

export const PatentDetailsModal = ({
  patent,
  open,
  onClose,
}: PatentDetailsModalProps) => {
  const { documents, totalCount, isLoading, error } = usePatentDocuments(
    patent?.applicationNumberText,
    open && !!patent
  );

  if (!patent) {
    return null;
  }

  const meta = patent.applicationMetaData;
  const inventors = meta.inventorBag
    ?.map((inventor) => inventor.inventorNameText)
    .filter(Boolean)
    .join(', ');
  const latestOfficialDate = getLatestOfficialDate(documents);

  return (
    <PatentDetailsDialog open={open} onClose={onClose}>
      <ModalBody>
        {/* Header: "Patent" heading + close button */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 1.5 }}
        >
          <Text variant="h5" sx={{ fontWeight: 700 }}>
            Patent
          </Text>
          <IconButton size="small" onClick={onClose} aria-label="Close details">
            <Close />
          </IconButton>
        </Stack>

        {/* Main bordered panel: type → title → app# → chips → metadata */}
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '20px',
            p: 2.5,
            mb: 2.5,
          }}
        >
          <ModalAppType sx={{ mb: 1 }}>
            {meta.applicationTypeLabelName || 'Patent Application'}
          </ModalAppType>

          <ModalTitle sx={{ mb: 0.5 }}>
            {meta.inventionTitle || 'Untitled Application'}
          </ModalTitle>

          <ModalApplicationNumber sx={{ mb: 1.5 }}>
            Application{' '}
            {formatPatentApplicationNumber(patent.applicationNumberText)}
            {meta.patentNumber ? ` | Patent ${meta.patentNumber}` : ''}
          </ModalApplicationNumber>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2.5 }}>
            {meta.applicationStatusDescriptionText && (
              <Chip
                label={meta.applicationStatusDescriptionText}
                size="small"
                variant="outlined"
              />
            )}
            <Chip
              label={
                isLoading
                  ? 'Loading...'
                  : `${totalCount} Document${totalCount === 1 ? '' : 's'}`
              }
              size="small"
              variant="outlined"
            />
            {latestOfficialDate && !isLoading && (
              <Chip
                label={`Latest Document ${formatPatentDate(
                  latestOfficialDate
                )}`}
                size="small"
                variant="outlined"
              />
            )}
          </Box>

          <Stack spacing={2}>
            <MetadataField label="Inventors" value={inventors} />
            <MetadataField label="Applicant" value={meta.firstApplicantName} />
            <MetadataField
              label="Filed"
              value={formatPatentDate(meta.filingDate)}
            />
            <MetadataField
              label="Grant Date"
              value={formatPatentDate(meta.grantDate)}
            />
          </Stack>
        </Box>

        {/* Documents tab heading */}
        <DocumentsTabHeading sx={{ mt: 3 }}>
          <Text
            variant="body2"
            sx={{
              fontWeight: 700,
              color: 'primary.main',
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
            }}
          >
            Documents
            {!isLoading && ` (${totalCount})`}
          </Text>
        </DocumentsTabHeading>

        {/* Documents content */}
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <Stack alignItems="center" spacing={1.5}>
              <CircularProgress />
              <Text variant="body2" color="text.secondary">
                Loading patent documents...
              </Text>
            </Stack>
          </Box>
        )}

        {!isLoading && error && (
          <Text
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: 'center', py: 2 }}
          >
            {error}
          </Text>
        )}

        {!isLoading && !error && documents.length === 0 && (
          <Text variant="body2" color="text.secondary">
            No documents are available for this application.
          </Text>
        )}

        {!isLoading && !error && documents.length > 0 && (
          <PatentDocumentsList documents={documents} />
        )}
      </ModalBody>
    </PatentDetailsDialog>
  );
};
